import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { contactInfo } from '../data/mock';

// Webhook N8N pour l'envoi des formulaires de contact
const N8N_WEBHOOK_URL = 'https://n8n.srv823553.hstgr.cloud/webhook/46db269b-0b0c-40c5-b81b-152abcc3b1e1';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventLocation: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Envoi direct au webhook N8N
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          event_date: formData.eventDate,
          event_location: formData.eventLocation,
          service: formData.service,
          message: formData.message,
          submitted_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }
      
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          eventLocation: '',
          service: '',
          message: ''
        });
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setError('Erreur lors de l\'envoi du formulaire. Veuillez réessayer ou nous contacter directement par téléphone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfoDisplay = [
    {
      icon: Mail,
      title: "Email",
      details: contactInfo.email,
      description: "Réponse sous 24h"
    },
    {
      icon: Phone,
      title: "Téléphone",
      details: contactInfo.phone,
      description: "Lun-Sam 9h-19h"
    },
    {
      icon: MapPin,
      title: "Zone d'intervention",
      details: contactInfo.location,
      description: "Déplacements possibles"
    },
    {
      icon: Clock,
      title: "Disponibilité",
      details: "7j/7 pour vos événements",
      description: "Weekend et jours fériés"
    }
  ];

  const services = [
    'Sax + DJ (Formule Premium)',
    'DJ Soirée',
    'Saxophone Cocktail',
    'Autre (préciser dans le message)'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 leading-tight">
            Contactez-nous
          </h1>
          <p className="text-xl text-warm-gray mb-8 max-w-3xl mx-auto leading-relaxed">
            Prêt à créer des souvenirs musicaux inoubliables ? Parlons de votre projet 
            et recevez un devis personnalisé sous 24h.
          </p>
        </div>
      </section>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-pearl/30">
                <CardHeader>
                  <CardTitle className="text-2xl text-charcoal">
                    Demande de devis
                  </CardTitle>
                  <p className="text-warm-gray">
                    Remplissez ce formulaire pour recevoir une proposition personnalisée
                  </p>
                </CardHeader>
                <CardContent>
                  {!isSubmitted ? (
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                          <p className="text-sm">{error}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-charcoal font-medium">
                            Nom complet *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                            required
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-charcoal font-medium">
                            Email *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                            required
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-charcoal font-medium">
                            Téléphone
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="eventDate" className="text-charcoal font-medium">
                            Date de l'événement
                          </Label>
                          <Input
                            id="eventDate"
                            name="eventDate"
                            type="date"
                            value={formData.eventDate}
                            onChange={handleInputChange}
                            className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                            data-testid="contact-date"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="eventLocation" className="text-charcoal font-medium">
                          Lieu de l'événement
                        </Label>
                        <Input
                          id="eventLocation"
                          name="eventLocation"
                          value={formData.eventLocation}
                          onChange={handleInputChange}
                          placeholder="Ville, département ou nom du lieu"
                          className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                          data-testid="contact-location"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-charcoal font-medium">
                          Service souhaité
                        </Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:border-blue-900 focus:ring-blue-900 focus:ring-1 bg-white"
                          data-testid="contact-service"
                        >
                          <option value="">Sélectionnez un service</option>
                          {services.map((service, index) => (
                            <option key={index} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-charcoal font-medium">
                          Message
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Décrivez votre événement, vos souhaits musicaux, nombre d'invités..."
                          rows={5}
                          className="border-warm-gray/30 focus:border-blue-900 focus:ring-blue-900"
                          data-testid="contact-message"
                        />
                      </div>

                      <Button 
                        type="submit"
                        size="lg"
                        className="w-full bg-blue-900 hover:bg-blue-800 text-white hover:shadow-lg transition-all duration-300"
                        disabled={isSubmitting}
                        data-testid="contact-submit"
                      >
                        <Send className="mr-2 h-5 w-5" />
                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                      </Button>

                      <p className="text-xs text-warm-gray text-center">
                        * Champs obligatoires. Réponse garantie sous 24h.
                      </p>
                    </form>
                  ) : (
                    <div className="text-center py-8" data-testid="contact-success">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-4 rounded-full w-16 h-16 mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-white mx-auto" />
                      </div>
                      <h3 className="text-xl font-bold text-charcoal mb-2">Message envoyé !</h3>
                      <p className="text-warm-gray">
                        Merci pour votre demande. Nous vous recontacterons très rapidement 
                        pour discuter de votre projet.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {contactInfoDisplay.map((info, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-ivory/50 to-pearl/50 hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 rounded-lg group-hover:shadow-lg transition-all duration-300">
                        <info.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-charcoal mb-1 group-hover:text-blue-900 transition-colors">
                          {info.title}
                        </h3>
                        <p className="text-charcoal font-medium mb-1">{info.details}</p>
                        <p className="text-sm text-warm-gray">{info.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* FAQ Quick */}
              <Card className="border-0 bg-gradient-to-br from-blue-50 to-blue-100/50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-charcoal mb-4">Questions fréquentes</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-charcoal">Délai de réservation ?</p>
                      <p className="text-warm-gray">Idéalement 6 mois avant l'événement</p>
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Zone d'intervention ?</p>
                      <p className="text-warm-gray">Île-de-France et régions limitrophes</p>
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Devis gratuit ?</p>
                      <p className="text-warm-gray">Oui, personnalisé et sans engagement</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
